/**
 * Toasts — did that work, or not.
 *
 * Until now a mutation that succeeded said nothing at all: the row changed and
 * you inferred the rest. That is survivable on a list you can see, and not
 * survivable on the ones you cannot — approving attendance from a filtered
 * queue removes the row you were looking at, which looks identical to the page
 * having lost your click.
 *
 * Three rules, and the third is the one carrying the weight now:
 *
 *   - **A success is polite; a failure is assertive.** Success is `role="status"`
 *     with `aria-live="polite"`, so it waits its turn. Failure is `role="alert"`,
 *     which interrupts — a screen reader user must not learn about a refused
 *     approval whenever the reader happens to get round to it. This is about
 *     *announcement*, not duration, and is unaffected by the rule below.
 *   - **Every toast clears itself after `AUTO_DISMISS_MS`**, failures included
 *     (Parinaam, 8 Oct 2026). Errors used to stay until dismissed, on the
 *     reasoning that an error is often the only place the reason exists and
 *     auto-dismissing it means the one person who looked away never learns why
 *     nothing happened. That reasoning has not become wrong — it has been
 *     overruled, and what makes the trade bearable is the rule below. If a
 *     failure is ever reported *only* by a toast, this decision needs
 *     revisiting rather than working around.
 *   - **A toast is never the only report.** Forms still render field errors
 *     inline (§8.40 wants the message where the mistake is); the toast says
 *     *something* went wrong so it cannot pass unnoticed. With errors now on a
 *     timer, this is what stops a five-second window being the only chance
 *     anybody had to read why.
 */

import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { ApiRequestError } from '../../services/api/client';
import styles from './Toast.module.css';

export type ToastTone = 'success' | 'error' | 'info';

interface Toast {
  id: number;
  tone: ToastTone;
  message: string;
}

interface ToastApi {
  /** Auto-dismisses: the screen behind it already shows the new state. */
  success: (message: string) => void;
  /**
   * Auto-dismisses too, since 8 Oct 2026 — so a caller must not treat this as
   * the lasting record of a failure. Accepts a thrown value, so a caller can
   * hand over whatever it caught without unwrapping it first.
   */
  error: (error: unknown, fallback?: string) => void;
  /**
   * Nothing happened, and that was the correct outcome — *"No changes to save."*
   *
   * This is its own tone rather than a success, because a green tick against
   * "no changes to save" tells a staff member their edit went through when it
   * did not. The distinction is the whole point: *saved* and *there was nothing
   * to save* look identical on the screen behind the toast, and the toast is the
   * only thing that separates them.
   */
  info: (message: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

/**
 * How long a toast that dismisses itself stays up.
 *
 * Five seconds, by Parinaam's instruction of 8 Oct 2026; four before that.
 * Long enough to read a sentence and look back at it, short enough not to sit
 * over the next action.
 *
 * Governs success *and* info — it was called `SUCCESS_MS` while doing both,
 * which read as though an info toast had a timer of its own somewhere. Errors
 * are the ones with no timer at all; see the rule at the top of this file.
 *
 * Exported so a test can pin it. The number is a decision, and the thing worth
 * asserting is that the toast is still there a moment before it and gone a
 * moment after — not that one file agrees with itself.
 */
export const AUTO_DISMISS_MS = 5000;

/*
 * There is deliberately no de-duplication here.
 *
 * "Details saved." appeared twice because React Query runs the hook's
 * `onSuccess` *and* the one handed to `mutate()`, and the screen reported the
 * outcome in both. The fix is that the hook owns the message and the call site
 * only closes the form — one owner, enforced by a test that counts.
 *
 * A time-windowed suppressor was tried here as a backstop and taken out again.
 * The gap between those two callbacks contains a full re-render, so the window
 * has to be wide enough to cover a slow one, and by then it is wide enough to
 * swallow a second genuine action — approving two rows really should say "Hours
 * approved." twice. A guard that collapses duplicates only sometimes turns a
 * reproducible bug into an intermittent one.
 */

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback(
    (tone: ToastTone, message: string) => {
      const id = nextId.current++;
      setToasts((current) => [...current, { id, tone, message }]);
      // Every tone, errors included (8 Oct 2026). Unconditional on purpose:
      // a `tone !== 'error'` here is exactly how the old rule would creep back.
      window.setTimeout(() => dismiss(id), AUTO_DISMISS_MS);
    },
    [dismiss]
  );

  const api = useMemo<ToastApi>(
    () => ({
      success: (message) => push('success', message),
      info: (message) => push('info', message),
      error: (error, fallback = 'Something went wrong. Please try again.') => {
        /**
         * A confirmable 409 is a question, not a failure.
         *
         * Plans D3 and D4 both answer the first attempt with a 409 on purpose —
         * *"they are not assigned to this activity, record it anyway?"*, *"they
         * are already committed at that time"* — and the screen turns it into a
         * prompt with a Continue button. Announcing "could not assign the
         * volunteer" over the top of that prompt contradicts it, and would train
         * staff to dismiss a toast that is wrong as often as it is right.
         *
         * The client already distinguishes them, so this reads the flag rather
         * than matching on message text.
         */
        if (error instanceof ApiRequestError && error.needsConfirmation) return;
        push('error', describe(error, fallback));
      },
    }),
    [push]
  );

  return (
    <ToastContext.Provider value={api}>
      {children}

      {/*
        One region, outside the routed page, so a toast survives the navigation
        that often follows the action that raised it.

        Rendered only when there is something in it. An always-present empty div
        is invisible but not free: it puts a node in every test's container and
        in the accessibility tree for no reason. Announcement does not depend on
        it — each toast carries its own `role`, and inserting a `role="alert"`
        announces whether or not its parent was already there.
      */}
      {toasts.length > 0 && (
        <div className={styles.region}>
          {toasts.map((toast) => (
            <div
              key={toast.id}
              className={`${styles.toast} ${styles[toast.tone]}`}
              role={toast.tone === 'error' ? 'alert' : 'status'}
              aria-live={toast.tone === 'error' ? 'assertive' : 'polite'}
            >
              <span className={styles.message}>{toast.message}</span>
              <button
                type="button"
                className={styles.dismiss}
                onClick={() => dismiss(toast.id)}
                aria-label="Dismiss"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </ToastContext.Provider>
  );
}

/**
 * Turn a thrown value into a sentence.
 *
 * An `ApiRequestError` already carries a message the API wrote for a person
 * (§5.10), so it is used as-is. Anything else is a frontend bug, and its raw
 * text would only confuse — the fallback says what the caller was trying to do.
 */
function describe(error: unknown, fallback: string): string {
  if (error instanceof ApiRequestError) return error.message;
  if (typeof error === 'string' && error.trim()) return error;
  return fallback;
}

/**
 * `const toast = useToast()`.
 *
 * Throws outside the provider rather than returning a no-op: a silent no-op
 * would mean a screen that reports nothing, which is the exact failure this
 * exists to remove, and it would only be noticed in production.
 */
export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error('useToast must be used inside <ToastProvider>.');
  return api;
}
